import React, { useState, useEffect, useMemo } from 'react'
import { Col, Row } from 'react-bootstrap'
import { PageTitle } from 'domain/feature/hub/pageTitle/styles'
import { showConfirmationPopUp } from 'components/popup/confirmationModal'
import { showToast } from 'components/toast'
import { postProvider, PostFormat, GetFormat, useGet, DeleteFormat, deleteProvider } from 'sharedKernel/api'
import { v4 as uuidv4 } from 'uuid'
import { Loading } from 'components/loading'
import { getCredLocalStorage } from 'sharedKernel/auth/credLocalStorage'
import * as S from './styles'
import { InviteForm } from './InviteForm'
import { SearchBar } from './SearchBar'
import { MembersTable } from './MembersTable'
import type { IMember } from './MembersTable'
import { PaginationControls } from './PaginationControls'
import { loggedUser } from 'sharedKernel/user/LoggedUser'

interface IMembersResponse {
  data: IMember[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

const MAX_MEMBERS = 5
const user = loggedUser.get();

export const HubCompanyCityHallMembersManagement = (): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [memberName, setMemberName] = useState('')
  const [emailValue, setEmailValue] = useState('')
  const { mutate: sendInvite } = postProvider()

  const buildMembersUrl = (): string => {
    const logged = getCredLocalStorage()
    const cityHallInfo = logged?.company?.cityHallInfo || logged?.cityHallInfo
    
    const params = new URLSearchParams()
    
    params.append('page', String(page))
    params.append('pageSize', String(pageSize))
    if (searchTerm.trim()) {
      params.append('search', searchTerm.trim())
    }
    return `/v1/hub/company/city-hall-members/${user.cityHallInfo?.Id}?${params.toString()}`
  }

  const membersRequest: GetFormat = {
    url: buildMembersUrl()
  }

  const { data: membersData, isFetching, refetch } = useGet<IMembersResponse>(
    membersRequest,
    `members-${page}-${pageSize}-${searchTerm}`
  )

  const members = useMemo<IMember[]>(() => {
    if (!membersData?.data) return []
    return membersData.data
  }, [membersData])

  const totalPages = membersData?.totalPages || 1
  const totalRows = membersData?.total || 0
  const hasReachedLimit = members.length >= MAX_MEMBERS

  useEffect(() => {
    setPage(1)
  }, [searchTerm])

  const handleAddMember = (): void => {
    if (!memberName.trim() || !emailValue.trim()) {
      showToast('error', 'Preencha o nome do membro e o e-mail')
      return
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(emailValue.trim())) {
      showToast('error', 'E-mail inválido')
      return
    }

    // Verificar limite
    if (hasReachedLimit) {
      showToast('error', `Você atingiu o limite máximo de ${MAX_MEMBERS} membros`)
      return
    }

    const cityHallId = user.cityHallInfo?.Id
    const inviteUrl = `/v1/hub/company/invite-city-hall`
    
    const payloadData = {
      cityHallId: cityHallId,
      email: emailValue.trim(),
      name: memberName.trim(),
      position: 'member'
    }
    
    const invitePost: PostFormat = {
      url: inviteUrl,
      data: {
        id: uuidv4(),
        data: {
          cityHallId: cityHallId,
          email: emailValue.trim(),
          name: memberName.trim(),
          position: 'member'
        }
      }
    }

    sendInvite(invitePost, {
      onSuccess: () => {
        showToast('success', 'Convite enviado com sucesso!')
        setMemberName('')
        setEmailValue('')
        refetch()
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao enviar convite'
        showToast('error', errorMessage)
      }
    })
  }

  const handleNotify = async (member: IMember): Promise<void> => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 500))
      showToast('success', 'Notificação reenviada com sucesso!')
      refetch()
    } catch (error) {
      showToast('error', 'Erro ao reenviar notificação')
    }
  }

  const onSuccessDeletion = async (): Promise<void> => {
      showToast('success', 'Membro removido com sucesso!')
      refetch()
    }

  const { mutate: deleteCityHall } = deleteProvider(onSuccessDeletion)
  
  const handleRemove = (member: IMember): void => {
      const request: DeleteFormat = {
        url: `/v1/system/user/${member.email}`,
      }
  
      showConfirmationPopUp({
        title: 'Remover Membro',
        message: (
          <span>
            Tem certeza de que deseja remover <strong>{member.name}</strong>?
          </span>
        ),
        onConfirm: () => {
          deleteCityHall(request)
        },
      })
    }

  return (
    <>
      <S.HeaderWrapper>
        <Row className="align-items-center">
          <Col>
            <PageTitle>Adicionar membros</PageTitle>
          </Col>
        </Row>
      </S.HeaderWrapper>

      <InviteForm
        memberName={memberName}
        emailValue={emailValue}
        hasReachedLimit={hasReachedLimit}
        maxMembers={MAX_MEMBERS}
        onChangeName={setMemberName}
        onChangeEmail={setEmailValue}
        onAdd={handleAddMember}
      />

      <SearchBar searchTerm={searchTerm} onChange={setSearchTerm} />

      {isFetching ? (
        <div className="d-flex justify-content-center py-5">
          <Loading />
        </div>
      ) : (
        <MembersTable
          members={members}
          onNotify={handleNotify}
          onRemove={handleRemove}
        />
      )}

      {!isFetching && (
        <PaginationControls
          page={page}
          totalPages={totalPages}
          totalRows={totalRows}
          onPrev={() => setPage(page - 1)}
          onNext={() => setPage(page + 1)}
        />
      )}
    </>
  )
}